import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DEV_TOOLS_ENABLED } from '../utils/devTools';
import { Wrench, Eye } from 'lucide-react';
import { toast } from 'react-toastify';

// ─── Dev Accounts from env vars ──────────────────────────────────

const DEV_ACCOUNTS = [
  {
    key: 'SA',
    label: 'SA',
    tooltip: 'SuperAdmin',
    color: '#e53e3e',
    email: import.meta.env.VITE_DEV_SA_EMAIL,
    password: import.meta.env.VITE_DEV_SA_PASSWORD,
  },
  {
    key: 'OA',
    label: 'OA',
    tooltip: 'OrgAdmin',
    color: '#d69e2e',
    email: import.meta.env.VITE_DEV_OA_EMAIL,
    password: import.meta.env.VITE_DEV_OA_PASSWORD,
  },
  {
    key: 'U',
    label: 'U',
    tooltip: 'User',
    color: '#38a169',
    email: import.meta.env.VITE_DEV_USER_EMAIL,
    password: import.meta.env.VITE_DEV_USER_PASSWORD,
  },
  {
    key: 'DA',
    label: 'DA',
    tooltip: 'Demo Admin (builder)',
    color: '#3182ce',
    icon: Wrench,
    divider: true,
    email: import.meta.env.VITE_DEV_DEMO_ADMIN_EMAIL,
    password: import.meta.env.VITE_DEV_DEMO_ADMIN_PASSWORD,
  },
  {
    key: 'DU',
    label: 'DU',
    tooltip: 'Demo User (read-only)',
    color: '#805ad5',
    icon: Eye,
    email: import.meta.env.VITE_DEV_DEMO_USER_EMAIL,
    password: import.meta.env.VITE_DEV_DEMO_USER_PASSWORD,
  },
].filter(a => a.email && a.password);

// ─── Styled Components ───────────────────────────────────────────

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1.5px dashed ${props => props.theme.colors.border};
  border-radius: 20px;
  padding: 4px 10px 4px 8px;
  margin-left: ${props => props.theme.spacing.sm};

  @media (max-width: 640px) {
    display: none;
  }
`;

const DevBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  user-select: none;
`;

const AccountButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.$active ? props.$roleColor + '18' : 'none'};
  border: 1px solid ${props => props.$active ? props.$roleColor + '60' : 'transparent'};
  border-radius: 12px;
  padding: 3px 8px 3px 6px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.4 : 1};
  transition: all 0.15s ease;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$active ? props.$roleColor : props.theme.colors.text};
  line-height: 1;

  &:hover:not(:disabled) {
    background: ${props => !props.$disabled && (props.$roleColor + '12')};
    border-color: ${props => !props.$disabled && (props.$roleColor + '40')};
  }
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  display: inline-block;
  flex-shrink: 0;
`;

const Divider = styled.span`
  width: 1px;
  height: 14px;
  background-color: ${props => props.theme.colors.border};
  margin: 0 2px;
  flex-shrink: 0;
`;

const Spinner = styled.span`
  width: 7px;
  height: 7px;
  border: 1.5px solid ${props => props.$color + '40'};
  border-top-color: ${props => props.$color};
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  animation: ${spin} 0.6s linear infinite;
`;

// ─── Component ───────────────────────────────────────────────────

export const DevAccountSwitcher = () => {
  const [switchingKey, setSwitchingKey] = useState(null);
  const theme = useTheme();
  const { user, login } = useAuth();
  const navigate = useNavigate();

  // Double guard: dev-tools flag (or localhost) + env vars present.
  // In production, both gates fail, so the component never renders.
  if (!DEV_TOOLS_ENABLED || DEV_ACCOUNTS.length === 0) return null;

  const handleSwitch = async (account) => {
    if (switchingKey || user?.email === account.email) return;

    setSwitchingKey(account.key);
    try {
      const result = await login(account.email, account.password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        toast.error(result.error || 'Login failed');
      }
    } catch (err) {
      console.error('Dev account switch failed:', err);
    } finally {
      setSwitchingKey(null);
    }
  };

  return (
    <Container theme={theme}>
      <DevBadge theme={theme}>DEV</DevBadge>
      {DEV_ACCOUNTS.map(account => {
        const isActive = user?.email?.toLowerCase() === account.email.toLowerCase();
        const isDisabled = switchingKey !== null && switchingKey !== account.key;
        const isLoading = switchingKey === account.key;

        const Icon = account.icon;

        return (
          <React.Fragment key={account.key}>
            {account.divider && <Divider theme={theme} />}
            <AccountButton
              theme={theme}
              $active={isActive}
              $disabled={isDisabled}
              $roleColor={account.color}
              disabled={isDisabled}
              onClick={() => handleSwitch(account)}
              title={`${account.tooltip} (${account.email})`}
            >
              {isLoading ? (
                <Spinner $color={account.color} />
              ) : Icon ? (
                <Icon size={11} color={account.color} />
              ) : (
                <Dot $color={account.color} />
              )}
              {account.label}
            </AccountButton>
          </React.Fragment>
        );
      })}
    </Container>
  );
};
